import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";

const TYPE_LABELS = {
  habitant: { fr: "Chez l'habitant", en: "Homestay" },
  hotel: { fr: "Hôtel", en: "Hotel" },
  gite: { fr: "Gîte", en: "B&B" },
  congregation: { fr: "Congrégation", en: "Community" },
};

const emptyOffer = () => ({
  type: "habitant", host_name: "", city: "", address: "", phone: "", email: "",
  capacity: 1, available_from: "", available_to: "", price_note: "", notes: "",
});

export default function HebergementTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState("");

  const load = useCallback(async () => {
    const [oRes, rRes] = await Promise.all([
      api.get("/admin/housing/offers"),
      api.get("/admin/housing/requests"),
    ]);
    setOffers(oRes.data || []);
    setRequests(rRes.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.host_name.trim() || !editing.city.trim()) return;
    const payload = { ...editing, capacity: Number(editing.capacity) || 1 };
    if (editing.id) {
      await api.patch(`/admin/housing/offers/${editing.id}`, payload);
    } else {
      await api.post("/admin/housing/offers", payload);
    }
    setEditing(null);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm(isFr ? "Supprimer cette offre ?" : "Delete this offer?")) return;
    await api.delete(`/admin/housing/offers/${id}`);
    load();
  };

  const stats = useMemo(() => {
    const byType = {};
    let totalCapacity = 0;
    let totalRemaining = 0;
    for (const o of offers) {
      byType[o.type] = (byType[o.type] || 0) + (o.capacity || 0);
      totalCapacity += o.capacity || 0;
      totalRemaining += o.remaining_capacity ?? o.capacity ?? 0;
    }
    const assigned = totalCapacity - totalRemaining;
    return { byType, totalCapacity, totalRemaining, assigned,
              pendingRequests: requests.filter((r) => !r.assigned_offer_id).length };
  }, [offers, requests]);

  const filtered = filterType ? offers.filter((o) => o.type === filterType) : offers;

  return (
    <div className="space-y-6" data-testid="hebergement-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl">{isFr ? "Hébergement" : "Accommodation"}</h2>
        <button className="ml-auto btn btn-primary" onClick={() => setEditing(emptyOffer())} data-testid="hebergement-add-btn">
          {isFr ? "+ Nouvelle offre" : "+ New offer"}
        </button>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        <Kpi label={isFr ? "Capacité totale" : "Total capacity"} value={stats.totalCapacity} color="var(--gold)" testId="heb-kpi-total" />
        <Kpi label={isFr ? "Affectés" : "Assigned"} value={stats.assigned} color="var(--cinnabar)" testId="heb-kpi-assigned" />
        <Kpi label={isFr ? "Disponible" : "Available"} value={stats.totalRemaining} color="var(--moss)" testId="heb-kpi-remaining" />
        <Kpi label={isFr ? "Demandes en attente" : "Pending requests"} value={stats.pendingRequests} color="var(--ink-2)" testId="heb-kpi-pending" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--muted)" }}>
          {isFr ? "Par type :" : "By type:"}
        </span>
        <button onClick={() => setFilterType("")} className="px-3 py-1 text-xs rounded-sm font-caps"
                style={{ letterSpacing: "0.15em",
                         backgroundColor: !filterType ? "var(--cinnabar)" : "var(--surface-2)",
                         color: !filterType ? "white" : "var(--ink)" }}
                data-testid="heb-filter-all">
          {isFr ? "Tous" : "All"} ({stats.totalCapacity})
        </button>
        {Object.entries(TYPE_LABELS).map(([k, l]) => (
          <button key={k} onClick={() => setFilterType(filterType === k ? "" : k)}
                  className="px-3 py-1 text-xs rounded-sm font-caps"
                  data-testid={`heb-filter-${k}`}
                  style={{ letterSpacing: "0.15em",
                           backgroundColor: filterType === k ? "var(--cinnabar)" : "var(--surface-2)",
                           color: filterType === k ? "white" : "var(--ink)" }}>
            {l[isFr ? "fr" : "en"]} ({stats.byType[k] || 0})
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {filtered.length === 0 && (
          <p className="font-body" style={{ color: "var(--muted)" }}>
            {isFr ? "Aucune offre d'hébergement enregistrée." : "No accommodation offers recorded."}
          </p>
        )}
        {filtered.map((o) => {
          const remaining = o.remaining_capacity ?? o.capacity;
          const used = (o.capacity || 0) - remaining;
          const pct = o.capacity ? Math.round((used / o.capacity) * 100) : 0;
          return (
            <div key={o.id} className="admin-card" data-testid={`heb-offer-${o.id}`}>
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-caps text-[0.65rem] px-2 py-0.5 rounded-sm"
                          style={{ backgroundColor: "var(--gold)", color: "white", letterSpacing: "0.18em" }}>
                      {TYPE_LABELS[o.type]?.[isFr ? "fr" : "en"] || o.type}
                    </span>
                    <h3 className="font-display text-lg">{o.host_name}</h3>
                    <span className="text-sm" style={{ color: "var(--ink-2)" }}>· {o.city}</span>
                  </div>
                  {o.address && <p className="text-xs mt-1" style={{ color: "var(--ink-2)" }}>{o.address}</p>}
                  {(o.phone || o.email) && (
                    <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                      {o.phone && <span>☎ {o.phone}</span>}
                      {o.phone && o.email && " · "}
                      {o.email && <span>✉ {o.email}</span>}
                    </p>
                  )}
                  {(o.available_from || o.available_to) && (
                    <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>
                      ⏱ {o.available_from || "?"} → {o.available_to || "?"}
                    </p>
                  )}
                  <div className="mt-2">
                    <div className="flex justify-between text-xs">
                      <span>{used} / {o.capacity} {isFr ? "occupés" : "used"}</span>
                      <span style={{ color: "var(--muted)" }}>{pct}%</span>
                    </div>
                    <div className="h-1.5 mt-1 rounded-sm overflow-hidden" style={{ backgroundColor: "var(--surface-2)" }}>
                      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "var(--cinnabar)" : "var(--gold)" }} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button className="btn btn-outline btn-sm" onClick={() => setEditing(o)} data-testid={`heb-edit-${o.id}`}>✎</button>
                  <button className="btn btn-outline btn-sm" onClick={() => remove(o.id)} data-testid={`heb-delete-${o.id}`}>✕</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <OfferEditor offer={editing} setOffer={setEditing} onSave={save} onClose={() => setEditing(null)} isFr={isFr} />
      )}
    </div>
  );
}

function Kpi({ label, value, color, testId }) {
  return (
    <div className="admin-card text-center" data-testid={testId}>
      <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>{label}</div>
      <div className="font-display text-3xl" style={{ color }}>{value}</div>
    </div>
  );
}

function OfferEditor({ offer, setOffer, onSave, onClose, isFr }) {
  const update = (k, v) => setOffer({ ...offer, [k]: v });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(20,16,12,0.7)" }}>
      <div className="admin-card max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-4" data-testid="heb-editor">
        <h3 className="font-display text-xl">{offer.id ? (isFr ? "Modifier l'offre" : "Edit offer") : (isFr ? "Nouvelle offre d'hébergement" : "New accommodation offer")}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Type" : "Type"}</span>
            <select className="input-classic w-full" value={offer.type} onChange={(e) => update("type", e.target.value)} data-testid="heb-input-type">
              {Object.entries(TYPE_LABELS).map(([k, l]) => <option key={k} value={k}>{l[isFr ? "fr" : "en"]}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Capacité" : "Capacity"}</span>
            <input type="number" className="input-classic w-full" value={offer.capacity} onChange={(e) => update("capacity", e.target.value)} data-testid="heb-input-capacity" />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Nom de l'hôte" : "Host name"}</span>
            <input className="input-classic w-full" value={offer.host_name} onChange={(e) => update("host_name", e.target.value)} data-testid="heb-input-host" />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Ville" : "City"}</span>
            <input className="input-classic w-full" value={offer.city} onChange={(e) => update("city", e.target.value)} data-testid="heb-input-city" />
          </label>
        </div>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Adresse" : "Address"}</span>
          <input className="input-classic w-full" value={offer.address} onChange={(e) => update("address", e.target.value)} />
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Téléphone" : "Phone"}</span>
            <input className="input-classic w-full" value={offer.phone} onChange={(e) => update("phone", e.target.value)} />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>Email</span>
            <input className="input-classic w-full" value={offer.email} onChange={(e) => update("email", e.target.value)} />
          </label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Disponible du" : "Available from"}</span>
            <input type="date" className="input-classic w-full" value={offer.available_from} onChange={(e) => update("available_from", e.target.value)} />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Au" : "To"}</span>
            <input type="date" className="input-classic w-full" value={offer.available_to} onChange={(e) => update("available_to", e.target.value)} />
          </label>
        </div>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Prix / Tarif" : "Price"}</span>
          <input className="input-classic w-full" value={offer.price_note} onChange={(e) => update("price_note", e.target.value)} />
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>Notes</span>
          <textarea rows={2} className="input-classic w-full" value={offer.notes} onChange={(e) => update("notes", e.target.value)} />
        </label>
        <div className="flex gap-2 pt-2">
          <button className="btn btn-primary" onClick={onSave} data-testid="heb-save-btn">{isFr ? "Enregistrer" : "Save"}</button>
          <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="heb-cancel-btn">{isFr ? "Annuler" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}
